import { computed, effect, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface layoutConfig {
    preset?: string;
    primary?: string;
    surface?: string | undefined | null;
    darkTheme?: boolean;
    menuMode?: string;
}

interface LayoutState {
    staticMenuDesktopInactive?: boolean;
    overlayMenuActive?: boolean;
    configSidebarVisible?: boolean;
    staticMenuMobileActive?: boolean;
    menuHoverActive?: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

const LAYOUT_CONFIG_STORAGE_KEY = 'pqg-layout-config';

function readStoredLayoutConfig(): Partial<layoutConfig> {
    try {
        const raw = localStorage.getItem(LAYOUT_CONFIG_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        return JSON.parse(raw) as Partial<layoutConfig>;
    } catch {
        return {};
    }
}

function writeStoredLayoutConfig(config: layoutConfig): void {
    try {
        localStorage.setItem(
            LAYOUT_CONFIG_STORAGE_KEY,
            JSON.stringify({
                preset: config.preset,
                primary: config.primary,
                surface: config.surface,
                darkTheme: config.darkTheme,
                menuMode: config.menuMode
            })
        );
    } catch {
        // ignore quota / private browsing
    }
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    _config: layoutConfig = {
        preset: 'Aura',
        primary: 'noir',
        surface: null,
        darkTheme: false,
        menuMode: 'static',
        ...readStoredLayoutConfig()
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false
    };

    readonly layoutConfig = signal<layoutConfig>(this._config);

    readonly layoutState = signal<LayoutState>(this._state);

    private configUpdate = new Subject<layoutConfig>();

    private overlayOpen = new Subject<any>();

    private menuSource = new Subject<MenuChangeEvent>();

    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();

    resetSource$ = this.resetSource.asObservable();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    readonly theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));

    readonly isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);

    readonly isDarkTheme = computed(() => this.layoutConfig().darkTheme);

    readonly getPrimary = computed(() => this.layoutConfig().primary);

    readonly getSurface = computed(() => this.layoutConfig().surface);

    readonly isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    readonly transitionComplete = signal<boolean>(false);

    private initialized = false;

    constructor() {
        this.layoutConfig.set({ ...this._config });
        this.toggleDarkMode(this._config);

        effect(() => {
            const config = this.layoutConfig();
            if (config) {
                writeStoredLayoutConfig(config);
                this.onConfigUpdate();
            }
        });

        effect(() => {
            const config = this.layoutConfig();
            if (!config) {
                return;
            }

            if (!this.initialized) {
                this.initialized = true;
                this.toggleDarkMode(config);
                return;
            }

            this.handleDarkModeTransition(config);
        });
    }

    private handleDarkModeTransition(config: layoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: layoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    toggleDarkMode(config?: layoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !this.layoutState().staticMenuMobileActive }));

            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }
}
